import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import Button from "../../shared/TailwindComponents/Button";
import Dropdown from "../../shared/TailwindComponents/Dropdown";
import { useTranslation } from "react-i18next";
import { fetchAllEmployees } from "../../../store/actions/organization/employeeActions";

interface TeamActionModalProps {
  action: "AddMember" | "LinkCase";
  existingMembers?: any[];
  selectedValue: string | string[];
  onConfirm: (ids: string[] | string) => void;
  onCancel: () => void;
}

const TeamActionModal: React.FC<TeamActionModalProps> = ({
  action,
  existingMembers = [],
  selectedValue,
  onConfirm,
  onCancel,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const allCases: any = [];
  const { employees } = useSelector((state: RootState) => state.employees);
  const [localSelectedValue, setLocalSelectedValue] = useState<string | string[]>(selectedValue);
  const [localOptions, setLocalOptions] = useState<{ label: string; value: string }[]>([]);
  const [localIsLoading, setLocalIsLoading] = useState(true);

  useEffect(() => {
    const fetchOptions = async () => {
      setLocalIsLoading(true);
      try {
        if (action === "LinkCase") {
          if (allCases.length > 0) {
            setLocalOptions(
              allCases.map((c: any) => ({
                label: c.CaseName,
                value: c.CaseID,
              }))
            );
          } 
        } else if (action === "AddMember") {
          if (employees.length > 0) {
            const existingIDs = new Set(existingMembers.map((e: any) => e.MemberID));
            const filtered = employees.filter((e: any) => !existingIDs.has(e.UserID));
            setLocalOptions(
              filtered.map((e: any) => ({
                label: `${e.FirstName} ${e.LastName} (${e.Email})`,
                value: e.UserID,
              }))
            );
          } else {
            const resp: any = await dispatch(fetchAllEmployees({ page: 1, limit: 50 }));
            if (resp) {
              const existingIDs = new Set(existingMembers.map((e: any) => e.MemberID));
              const filtered = resp.filter((e: any) => !existingIDs.has(e.UserID));
              setLocalOptions(
                filtered.map((e: any) => ({
                  label: `${e.FirstName} ${e.LastName} (${e.Email})`,
                  value: e.UserID,
                }))
              );
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch options", error);
      } finally {
        setLocalIsLoading(false);
      }
    };

    fetchOptions();
  }, [action, dispatch, allCases, employees, existingMembers]);

  const handleSubmit = () => {
    if (action === "LinkCase" && localSelectedValue) {
      onConfirm(localSelectedValue as string);
    } else if (action === "AddMember" && Array.isArray(localSelectedValue) && localSelectedValue.length) {
      onConfirm(localSelectedValue);
    }
    onCancel();
  };

  if (localIsLoading) {
    return (
      <div className="p-4 max-w-md mx-auto">
        <div className="col-span-3 bg-white dark:bg-gray-800 p-4 mt-4 dark:border-gray-700 animate-pulseFade space-y-1">
          <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      </div>
    );
  }

  const isMultiSelect = action === "AddMember";

  return (
    <div className="mx-auto space-y-4">
        <div className="px-4">
      <Dropdown
        options={localOptions}
        value={localSelectedValue}
        onChange={(val: any) => setLocalSelectedValue(val)}
        placeholder={action === "LinkCase" ? t("Select a case") : t("Select one or more members")}
        multiple={isMultiSelect}
        className="mb-6"
      />
      </div>
      <div className="flex justify-end gap-2 px-4 border-1 border-t border-gray-300 dark:border-gray-700 pt-6 mt-6">
        <Button variant="neutral" size="small" onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button
          variant="primary"
          size="small"
          onClick={handleSubmit}
          disabled={!localSelectedValue || (Array.isArray(localSelectedValue) && !localSelectedValue.length)}
        >
          {action === "LinkCase" ? t("Link") : t("Add")}
        </Button>
      </div>
    </div>
  );
};

export default TeamActionModal;